"use client";

import { useState, useEffect } from "react";
import { fetchWithAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router-dom";

interface ExternalImageCitizen {
  citizen_id: number;
  citizen_name: string;
  slug: string;
  main_pic: string | null;
  created_date: string;
}

export default function ExternalImagesPage() {
  const navigate = useNavigate();
  const [citizens, setCitizens] = useState<ExternalImageCitizen[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(1);
  const limit = 10;

  const fetchExternalImages = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams();

      if (debouncedSearchQuery) {
        queryParams.append("searchTerm", debouncedSearchQuery);
      }

      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());

      const response = await fetchWithAuth(
        `/admin/images/external?${queryParams.toString()}`
      );

      setCitizens(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (error) {
      toast.error("Failed to fetch external images");
      setCitizens([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExternalImages();
  }, [debouncedSearchQuery, page]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">External Images</h1>
          <p className="text-muted-foreground">
            Citizens with externally hosted images
          </p>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Created Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-12 w-12 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : citizens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  No citizens with external images found
                </TableCell>
              </TableRow>
            ) : (
              citizens.map((citizen) => (
                <TableRow key={citizen.citizen_id}>
                  <TableCell>
                    {citizen.main_pic ? (
                      <div className="relative h-12 w-12 overflow-hidden rounded-md">
                        <img
                          src={citizen.main_pic}
                          alt={citizen.citizen_name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="relative h-12 w-12 overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No image</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {citizen.citizen_name}
                  </TableCell>
                  <TableCell>
                    {format(new Date(citizen.created_date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/citizens/${citizen.citizen_id}`)
                      }
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-end space-x-2 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
          >
            Previous
          </Button>
          <div className="text-sm text-muted-foreground">
            Page {page} of {Math.ceil(total / limit)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(total / limit) || isLoading}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
